import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import smoothScroll from "../../utils/animateScroll";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentRowScrollingWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 1;
  flex-grow: 1;
`;

const ContentRowScrollingContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
`;

const ContentRowWrapper = styled.div`
  margin-bottom: 5rem;
  position: relative;
  border-radius: 20px;
  padding: 20px;
  padding-left: 0;
  margin-right: 2rem;
  margin-left: 2rem;
`;

const ContentRowTitle = styled.h2`
  color: white;
  margin: 0;
  margin-bottom: 22px;
  font-size: 27px;
  font-weight: 700;
  padding-left: 60px;
`;

const CardContainer = styled.div`
  width: calc(33% - 4px);
  height: 330px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  z-index: ${props => props.$focused ? 10 : 0};
  margin: 2px;
  cursor: pointer;
`;

const FocusBorderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: 20px;
  pointer-events: none;
  z-index: 20;
`;

const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$focused ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  flex-direction: column;
  gap: 1rem;
  z-index: 15;
`;

const Title = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
`;

const Year = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
`;

const ContentItem = ({ item = {}, onFocus, isFirst }) => {
  const navigate = useNavigate();
  const { slug, contentPhotos, title, year, tags } = item;

  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      navigate(`/movie/${slug}`);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        setFocus("MENU");
        return false;
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support
  const imageData = getOptimizedImageForType("horizontalSportCover", contentPhotos, "TV", {
    quality: 90,
    width: 420,
    format: "jpeg",
  }) ||
    getOptimizedImageForType("horizontalCover", contentPhotos, "TV", {
      quality: 90,
      width: 420,
      format: "jpeg",
    });

  return (
    <CardContainer
      ref={ref}
      $focused={focused}
      onClick={() => navigate(`/movie/${slug}`)}
      onMouseEnter={handleMouseEnter}
    >
      <OptimizedImage
        src={imageData.src}
        className="absolute inset-0 w-full h-full"
        style={{
          objectFit: 'cover',
          borderRadius: "20px",
        }}
      />
      <FocusBorderOverlay $focused={focused} />
      <ContentOverlay $focused={focused}>
        <Title>{title}</Title>
        <Year>{year}</Year>
      </ContentOverlay>
      {tags?.[0] && <ContentTag label={tags?.[0]} />}
    </CardContainer>
  );
};

export const Topabakh = ({ movies, onFocus, rowTitle, id, index }) => {
  const { ref, focusKey } = useFocusable({
    onFocus,
    focusKey: "topabakh_" + id,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
  });

  const scrollingRef = useRef(null);
  const { isMouseFocus } = useInputMode();
  const onAssetFocus = useCallback(
    (props) => {
      if (isMouseFocus) return;
      const { y, height } = props;
      const targetScroll = y > height - 200 ? y - 200 : 0;
      smoothScroll(scrollingRef, targetScroll, 500);
    },
    [scrollingRef, isMouseFocus]
  );

  if (movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} tabIndex={index}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper ref={scrollingRef}>
          <ContentRowScrollingContent>
            {movies?.map((item, idx) => {
              return (
                <ContentItem
                  item={item.content}
                  key={"tp_" + item.content.slug}
                  onFocus={onAssetFocus}
                  isFirst={idx === 0}
                />
              );
            })}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
